import { DescriptionOutlined } from '@mui/icons-material'
import { catalogColumnType, catalogFilterColumnType, catalogItemType, catalogTabType } from '../catalogTypes'
import { itesArLineType } from 'src/types'
import { COLUMN_CONFIG_TR } from 'src/EditQuote/steps/validate/TechnicalRequest'

export interface itesItemType extends catalogItemType {
  transactionType: string
  contractNumber: string
  catalogNumber: string
  meiComponent: string
  majorEndItem: string
  catalogClin: string
  vendorPartNumber: string
  listPrice: number
  ites4hSellPrice: number
  percentOffListPrice: number
  unspsc: string
  comment: string
  standards: string
}

const ALL_ITES_COLUMNS = [
  { key: 'transactionType', label: 'Transaction Type' },
  { key: 'contractNumber', label: 'Contract Number' },
  { key: 'catalogNumber', label: 'Catalog Number' },
  { key: 'meiComponent', label: 'MEI Component' },
  { key: 'majorEndItem', label: 'Major End Item' },
  { key: 'catalogClin', label: 'Catalog CLIN' },
  { key: 'vendorPartNumber', label: 'Vendor Part Number' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'mfrPartNumber', label: 'Manufacturer Part Number' },
  { key: 'description', label: 'Description' },
  { key: 'listPrice', label: 'List Price' },
  { key: 'ites4hSellPrice', label: 'ITES-4H Sell Price' },
  { key: 'percentOffListPrice', label: 'Percent Off List Price' },
  { key: 'unspsc', label: 'UNSPSC' },
  { key: 'comment', label: 'Comment' },
  { key: 'standards', label: 'Standards' },
]

export const AR_ITES_COLUMNS: catalogColumnType[] = [
  { key: 'check' as keyof itesArLineType, label: '', width: '48px', sticky: { left: '0px' } },
  { key: 'CatalogClin', label: 'Catalog CLIN', width: '200px', sticky: { left: '48px' } },
  { key: 'QuoteNumber', label: 'Quote #', width: '150px' },
  { key: 'QuoteOwner', label: 'Quote Owner', width: '250px' },
  {
    key: 'TransactionType',
    label: 'Transaction Type',
    width: '150px',
    editable: true,
    options: [{ label: 'A', value: 'A' }],
  },
  { key: 'ContractNumber', label: 'Contract Number', width: '150px' },
  {
    key: 'CatalogNumber',
    label: 'Catalog Number',
    width: '150px',
    editable: true,
    options: COLUMN_CONFIG_TR['Catalog Number'].options,
  },
  {
    key: 'MeiComponent',
    label: 'MEI Component',
    width: '150px',
    editable: true,
    options: COLUMN_CONFIG_TR['MEI Component'].options,
  },
  {
    key: 'MajorEndItem',
    label: 'Major End Item',
    width: '150px',
    editable: true,
    options: COLUMN_CONFIG_TR['Major End Item'].options,
  },
  { key: 'VendorPartNumber', label: 'Vendor Part Number', width: '150px' },
  {
    key: 'Manufacturer',
    label: 'Manufacturer',
    width: '150px',
    editable: true,
    inputType: 'autocomplete',
    source: 'manufacturer',
  },
  { key: 'MfrPartNumber', label: 'Manufacturer Part Number', width: '150px' },
  { key: 'Description', label: 'Description', width: '200px', editable: true },
  { key: 'ListPrice', label: 'List Price', width: '150px', inputType: 'currency', editable: true },
  { key: 'Ites4hSellPrice', label: 'ITES-4H Sell Price', width: '150px', inputType: 'currency' },
  {
    key: 'PercentOffListPrice',
    label: 'Percent Off List Price',
    width: '150px',
    inputType: 'percentage',
    editable: true,
  },
  { key: 'Unspsc', label: 'Unspsc', width: '150px', editable: true },
]

const AR_AWAITING_COLUMNS: catalogColumnType[] = [
  { key: 'ArNumber', label: 'AR Number', width: '100px' },
  { key: 'ContractNumber', label: 'Contract Number', width: '100px' },
  { key: 'TotalClins', label: 'Total Clins', width: '100px' },
  { key: 'ExportedFile', label: 'Exported File', width: '100px' },
]

const AR_APPROVED_COLUMNS: catalogColumnType[] = [
  ...AR_AWAITING_COLUMNS.filter(col => col.key !== 'ExportedFile'),
  { key: 'ItesEmail', label: 'ITES Approval Email', width: '150px' },
]

export const itesColumnMapping = new Map<string, catalogColumnType[]>([
  ['w819tc25da023', ALL_ITES_COLUMNS],
  ['w819tc25da023:ARS (Approved)', AR_APPROVED_COLUMNS],
  ['w819tc25da023:ARS (Awaiting)', AR_AWAITING_COLUMNS],
  ['w819tc25da023:ARS (Pending)', AR_ITES_COLUMNS],
])

export const itesTabGroups = new Map<string, catalogTabType[]>([
  [
    'w819tc25da023',
    [
      { label: 'W819TC25DA023', icon: <DescriptionOutlined />, source: 'w819tc25da023' },
      {
        label: 'ARS (Approved)',
        source: 'w819tc25da023/tr/headers',
        filters: { Status: 'Approved' },
        isArHeader: true,
      },
      {
        label: 'ARS (Awaiting)',
        source: 'w819tc25da023/tr/headers',
        filters: { Status: 'Awaiting' },
        isArHeader: true,
      },
      { label: 'ARS (Pending)', source: 'w819tc25da023/tr', filters: { Status: 'Pending' } },
    ],
  ],
])

export const itesColumnFilter = new Map<string, catalogFilterColumnType[]>([
  [
    'w819tc25da023',
    [
      { key: 'transactionType', label: 'Transaction Type' },
      { key: 'contractNumber', label: 'Contract Number' },
      { key: 'catalogNumber', label: 'Catalog Number' },
      { key: 'meiComponent', label: 'MEI Component' },
      { key: 'majorEndItem', label: 'Major End Item' },
      { key: 'catalogClin', label: 'Catalog CLIN' },
      { key: 'vendorPartNumber', label: 'Vendor Part Number' },
      { key: 'listPrice', label: 'List Price' },
      { key: 'ites4hSellPrice', label: 'ITES-4H Sell Price' },
      { key: 'percentOffListPrice', label: 'Percent Off List Price' },
      { key: 'unspsc', label: 'UNSPSC' },
      { key: 'comment', label: 'Comment' },
      { key: 'standards', label: 'Standards' },
    ],
  ],
])
